package ai.jessie.web.customers

import ai.jessie.web.theme.FieldErrorStyle
import ai.jessie.web.theme.FieldInputStyle
import ai.jessie.web.theme.FieldOptionalStyle
import ai.jessie.web.theme.FieldStyle
import ai.jessie.web.theme.RowStyle
import androidx.compose.runtime.Composable
import com.varabyte.kobweb.silk.style.toAttrs
import org.jetbrains.compose.web.attributes.AttrsScope
import org.jetbrains.compose.web.attributes.InputType
import org.jetbrains.compose.web.attributes.forId
import org.jetbrains.compose.web.attributes.selected
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Input
import org.jetbrains.compose.web.dom.Label
import org.jetbrains.compose.web.dom.Option
import org.jetbrains.compose.web.dom.P
import org.jetbrains.compose.web.dom.Select
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.Text

// `name` is the server's field path, so a returned problem marks exactly this control.
internal data class CustomerField(
    val name: String,
    val label: String,
    val value: String,
    val problem: String? = null,
)

@Composable
internal fun customerField(field: CustomerField, change: (String) -> Unit) {
    val id = "customer-${field.name}"
    fieldBlock(field, id) {
        Input(
            InputType.Text,
            attrs =
                FieldInputStyle.toAttrs {
                    id(id)
                    value(field.value)
                    fieldAttributes(field)
                    onInput { change(it.value) }
                },
        )
    }
}

@Composable
internal fun customerSelect(
    field: CustomerField,
    options: Map<String, String>,
    change: (String) -> Unit,
) {
    val id = "customer-${field.name}"
    fieldBlock(field, id) {
        Select(
            attrs =
                FieldInputStyle.toAttrs {
                    id(id)
                    fieldAttributes(field)
                    onChange { change(it.value.orEmpty()) }
                }
        ) {
            (mapOf("" to "Bitte wählen") + options).forEach { (value, label) ->
                Option(value, attrs = { if (value == field.value) selected() }) { Text(label) }
            }
        }
    }
}

@Composable
internal fun customerCheckbox(
    name: String,
    label: String,
    value: Boolean,
    change: (Boolean) -> Unit,
) {
    val id = "customer-$name"
    Div(attrs = RowStyle.toAttrs()) {
        Input(
            InputType.Checkbox,
            attrs = {
                id(id)
                attr("name", name)
                checked(value)
                onChange { change(it.value) }
            },
        )
        Label(attrs = { forId(id) }) { Text(label) }
    }
}

// The label text is unchanged; a trailing parenthesis such as "(optional)" is rendered in the
// quieter suffix style.
@Composable
private fun fieldBlock(field: CustomerField, id: String, control: @Composable () -> Unit) {
    Div(attrs = FieldStyle.toAttrs()) {
        Label(attrs = { forId(id) }) {
            val hint = field.label.indexOf(" (")
            if (hint < 0) Text(field.label)
            else {
                Text(field.label.substring(0, hint + 1))
                Span(attrs = FieldOptionalStyle.toAttrs()) { Text(field.label.substring(hint + 1)) }
            }
        }
        control()
        field.problem?.let { P(attrs = FieldErrorStyle.toAttrs()) { Text(it) } }
    }
}

private fun AttrsScope<*>.fieldAttributes(field: CustomerField) {
    attr("name", field.name)
    attr("autocomplete", "off")
    if (field.problem != null) {
        attr("aria-invalid", "true")
        attr("aria-describedby", "customer-problems")
    }
}
